import { Card } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { server } from "../../server";
import {
  RiFileChartLine,
  RiSearchLine,
  RiArrowUpDownLine,
  RiCoinLine,
  RiCalendarLine,
  RiTimeLine,
  RiUserLine,
  RiTrophyLine,
  RiHistoryLine
} from "react-icons/ri";

export default function AuctionReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Separate state for each parameter
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(500);
  const [sortBy, setSortBy] = useState("heighestAuctionBidAmount");
  const [sortOrder, setSortOrder] = useState("desc");
  const [liveAuction, setLiveAuction] = useState(false);

  const getReports = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/shop/report`, {
        params: {
          pageNo: pageNo,
          pageSize: pageSize,
          sortBy: sortBy,
          sortOrder: sortOrder,
          liveAuction: liveAuction,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
      });
      setReports(data?.data || []);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.message || e?.message || "Failed to fetch reports");
    }
  };

  useEffect(() => {
    getReports();
  }, [pageNo, pageSize, sortBy, sortOrder, liveAuction]);

  const filteredReports = reports.filter(
    (report) =>
      report.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.shopCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    const date = new Date(dateTimeString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const sortOptions = [
    { value: "heighestAuctionBidAmount", label: "Highest Bid" },
    { value: "basePrice", label: "Base Price" },
    { value: "bidDate", label: "Bid Date" },
    { value: "createdAt", label: "Created Date" },
    { value: "shopName", label: "Shop Name" },
  ];

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setPageNo(1);
  };

  return (
    <div className="flex justify-center w-full p-4 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="w-full max-w-7xl">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-3 rounded-xl shadow-lg">
              <RiFileChartLine className="text-3xl text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-800 tracking-tight">
                Auction Reports
              </h1>
              <p className="text-slate-500 mt-1">
                Comprehensive auction performance analytics
              </p>
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <RiSearchLine className="text-slate-400 text-lg" />
              </div>
              <input
                type="text"
                placeholder="Search by shop name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 w-full border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 placeholder-slate-400 shadow-sm transition-all"
              />
            </div>

            {/* Sort By */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPageNo(1);
                }}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 shadow-sm transition-all appearance-none cursor-pointer"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    Sort by: {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Order Toggle */}
            <button
              onClick={toggleSortOrder}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all shadow-sm"
            >
              <RiArrowUpDownLine className="text-lg text-blue-500" />
              <span className="font-medium text-slate-700">
                {sortOrder === "asc" ? "Ascending" : "Descending"}
              </span>
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card
            sx={{ borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
            className="p-4 bg-gradient-to-br from-blue-50 to-blue-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-semibold uppercase">Total Shops</p>
                <p className="text-2xl font-bold text-blue-800 mt-1">{reports.length}</p>
              </div>
              <RiTrophyLine className="text-3xl text-blue-500" />
            </div>
          </Card>

          <Card
            sx={{ borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
            className="p-4 bg-gradient-to-br from-green-50 to-green-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-semibold uppercase">Total Bids</p>
                <p className="text-2xl font-bold text-green-800 mt-1">
                  ₹{reports.reduce((sum, r) => sum + (r.heighestAuctionBidAmount || 0), 0).toLocaleString("en-IN")}
                </p>
              </div>
              <RiCoinLine className="text-3xl text-green-500" />
            </div>
          </Card>

          <Card
            sx={{ borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
            className="p-4 bg-gradient-to-br from-purple-50 to-purple-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-semibold uppercase">Avg Bid</p>
                <p className="text-2xl font-bold text-purple-800 mt-1">
                  ₹{reports.length > 0 ? Math.round(reports.reduce((sum, r) => sum + (r.heighestAuctionBidAmount || 0), 0) / reports.length).toLocaleString("en-IN") : 0}
                </p>
              </div>
              <RiCoinLine className="text-3xl text-purple-500" />
            </div>
          </Card>

          <Card
            sx={{ borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
            className="p-4 bg-gradient-to-br from-orange-50 to-orange-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-semibold uppercase">Highest Bid</p>
                <p className="text-2xl font-bold text-orange-800 mt-1">
                  ₹{Math.max(...reports.map(r => r.heighestAuctionBidAmount || 0)).toLocaleString("en-IN")}
                </p>
              </div>
              <RiTrophyLine className="text-3xl text-orange-500" />
            </div>
          </Card>
        </div>

        {/* Reports Table */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
          className="bg-white"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-slate-600">
              <thead className="text-xs text-slate-700 uppercase bg-gradient-to-r from-blue-50 to-blue-100 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 font-bold">Rank</th>
                  <th className="px-6 py-4 font-bold">Shop Details</th>
                  <th className="px-6 py-4 font-bold">Base Price</th>
                  <th className="px-6 py-4 font-bold">Highest Bid</th>
                  <th className="px-6 py-4 font-bold">Bidder</th>
                  <th className="px-6 py-4 font-bold">Auction Schedule</th>
                  <th className="px-6 py-4 font-bold">Bid Time</th>
                  <th className="px-6 py-4 font-bold text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <svg className="animate-spin h-12 w-12 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-slate-500 font-medium">Loading reports...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredReports.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="bg-slate-100 p-6 rounded-full mb-4">
                          <RiFileChartLine className="text-4xl text-slate-400" />
                        </div>
                        <p className="text-slate-800 font-medium text-lg">No reports found</p>
                        <p className="text-slate-500 text-sm mt-1">
                          {searchTerm ? `No matches for "${searchTerm}"` : "No auction data available"}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredReports.map((report, index) => (
                    <tr
                      key={report.id || report.guid}
                      className="bg-white border-b border-slate-100 hover:bg-blue-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold text-sm">
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-bold text-slate-800">{report.shopName}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Code: <span className="font-medium">{report.shopCode}</span>
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <RiCoinLine className="text-slate-400" />
                          <span className="font-semibold text-slate-700">
                            ₹{report.basePrice?.toLocaleString("en-IN") || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <RiTrophyLine className="text-green-500" />
                          <span className="font-bold text-green-600">
                            ₹{report.heighestAuctionBidAmount?.toLocaleString("en-IN") || "No bids"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <RiUserLine className="text-blue-500" />
                          <span className="text-slate-700">
                            {report.biddedByName ? `${report.biddedByName}` : "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-xs">
                            <RiCalendarLine className="text-blue-500" />
                            <span className="text-slate-600">{formatDate(report.bidDate)}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            <RiTimeLine className="text-purple-500" />
                            <span className="text-slate-600">
                              {formatTime(report.openTime)} - {formatTime(report.closeTime)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs text-slate-500">
                          {formatDateTime(report.biddedAt)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => navigate("/my/bidDetails", { state: { shop: { id: report.shopId || report.id, shopName: report.shopName, shopCode: report.shopCode } } })}
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all duration-200 border border-blue-100 shadow-sm mx-auto group"
                          title="View Bid History"
                        >
                          <RiHistoryLine className="text-lg group-hover:scale-110 transition-transform" />
                          <span className="text-xs font-semibold">History</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && filteredReports.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
              <span className="text-sm text-slate-600 font-medium">
                Showing {filteredReports.length} reports
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPageNo((prev) => Math.max(1, prev - 1))}
                  disabled={pageNo === 1}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-slate-600 font-medium flex items-center">
                  Page {pageNo}
                </span>
                <button
                  onClick={() => setPageNo((prev) => prev + 1)}
                  disabled={filteredReports.length < pageSize}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
